// src/pages/PodcastDetailsPage.js
import React, { useState, useEffect, useContext } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { doc, setDoc, getDoc, deleteDoc } from "firebase/firestore";
import './PodcastDetailsPage.css';
import { AudioContext } from '../contexts/AudioContext';
import { useAuth } from '../contexts/AuthContext';
import Header from '../components/Header';
import LoadingSpinner from '../components/LoadingSpinner';
import { getPodcastWithEpisodes } from '../utils/apiHelpers';
import { trackEvent, AnalyticsEvents } from '../utils/analytics';

const PodcastDetailsPage = () => {
  const { podcastId } = useParams();
  const navigate = useNavigate();

  const [podcastInfo, setPodcastInfo] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isFavourite, setIsFavourite] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const { currentUser } = useAuth(); // Use centralized auth context

  // Get audio context values
  const { playEpisode, currentEpisode: globalCurrentEpisode, isPlaying: globalIsPlaying } = useContext(AudioContext);

  // Effect for fetching podcast and episode data (combined single call)
  useEffect(() => {
    if (!podcastId) {
      setError("No Podcast ID provided.");
      setIsLoading(false);
      return;
    }

    const fetchPodcastData = async () => {
      setIsLoading(true);
      setError(null);
      setPodcastInfo(null);
      setEpisodes([]);

      try {
        // Combined API call - fetches both podcast info and episodes in one go
        const { podcastInfo: info, episodes: eps } = await getPodcastWithEpisodes(podcastId, 10);
        setPodcastInfo(info);
        setEpisodes(eps);

        // Track page view once loaded
        if (info) {
          trackEvent(AnalyticsEvents.PAGE_VIEW, {
            type: 'podcast_details',
            podcastId: info.id,
            podcastTitle: info.title
          }, currentUser?.uid);
        }
      } catch (err) {
        console.error("Error fetching podcast data:", err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPodcastData();
  }, [podcastId]);

  // Effect for checking favourite status
  useEffect(() => {
    if (!currentUser || !podcastInfo || !podcastInfo.id) {
      setIsFavourite(false); return;
    }
    const favouriteRef = doc(db, "users", currentUser.uid, "favourites", String(podcastInfo.id));
    const checkFavouriteStatus = async () => {
      try {
        const docSnap = await getDoc(favouriteRef);
        setIsFavourite(docSnap.exists());
      } catch (error) { console.error("Error checking favourite status:", error); }
    };
    checkFavouriteStatus();
  }, [currentUser, podcastInfo]);

  // handleToggleFavourite function
  const handleToggleFavourite = async () => {
    if (!currentUser) { alert("Please log in to manage favourites."); navigate('/'); return; }
    if (!podcastInfo || !podcastInfo.id) { alert("Podcast data is not fully loaded yet for favourites."); return; }
    const currentPodcastIdStr = String(podcastInfo.id);
    const favouriteRef = doc(db, "users", currentUser.uid, "favourites", currentPodcastIdStr);
    try {
      if (isFavourite) {
        await deleteDoc(favouriteRef); setIsFavourite(false); console.log("Removed from favourites");
      } else {
        await setDoc(favouriteRef, {
          id: podcastInfo.id,
          title: podcastInfo.title || 'No Title',
          image: podcastInfo.image || podcastInfo.artwork || '',
          author: podcastInfo.author || 'N/A',
          feedUrl: podcastInfo.url || podcastInfo.feedUrl || '',
          addedDate: new Date()
        });
        setIsFavourite(true); 
        console.log("Added to favourites");
        
        trackEvent(AnalyticsEvents.PODCAST_FOLLOW, {
          podcastId: podcastInfo.id,
          podcastTitle: podcastInfo.title
        }, currentUser.uid);
      }
    } catch (error) { console.error("Error toggling favourite: ", error); alert("Failed to update favourites. Please try again."); }
  };

  if (isLoading) {
    return (
      <>
        <Header />
        <LoadingSpinner message="Loading podcast details..." />
      </>
    );
  }
  if (error) {
    return <div className="error-message" style={{ padding: '20px' }}>Error: {error} <Link to="/home">Go Home</Link></div>;
  }
  if (!podcastInfo) {
    return <div style={{ padding: '20px' }}>Podcast details could not be loaded. <Link to="/home">Go Home</Link></div>;
  }

  return (
    <>
      <Header />
      <div className="podcast-details-container">
        <div className="details-content-wrapper">
          <div className="left-column">
            <div className="page-header">
              <span onClick={() => navigate(-1)} className="back-arrow" title="Go back">
                &larr;
              </span>
              <h1 className="podcast-title-header">{podcastInfo.title || 'Podcast Details'}</h1>
            </div>
            <div className='episodes-list-container'>
              {episodes.length > 0 ? (
                episodes.map((episode) => (
                  <div
                    key={episode.id}
                    className="episode-item"
                    onClick={() => {
                      if (episode && episode.enclosureUrl) {
                        playEpisode(episode);
                      } else {
                        alert("Audio for this episode is not available.");
                      }
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    <img
                      src={episode.image || podcastInfo.image || 'https://via.placeholder.com/60?text=Ep'}
                      alt={episode.title || 'Episode art'}
                      className="episode-item-image"
                    />
                    <div className="episode-item-info">
                      <h4>{episode.title || 'No Title'}</h4>
                      <p className="episode-description-snippet">
                        {(episode.description || 'No description for this episode.').replace(/<[^>]+>/g, '').substring(0, 120) + '...'}
                      </p>
                      <div className="episode-item-bottom">
                        <span className="episode-item-meta">
                          {episode.datePublishedPretty && <span>{episode.datePublishedPretty}</span>}
                          {episode.datePublishedPretty && episode.duration && episode.duration > 0 && " • "}
                          {episode.duration && episode.duration > 0 && <span>{Math.floor(episode.duration / 60)} min</span>}
                        </span>
                        <button
                          className="episode-play-button"
                          onClick={e => {
                            e.stopPropagation();
                            if (episode && episode.enclosureUrl) {
                              playEpisode(episode);
                            } else {
                              alert("Audio for this episode is not available.");
                            }
                          }}
                        >
                          {globalCurrentEpisode?.enclosureUrl === episode.enclosureUrl && globalIsPlaying ? 'Pause' : 'Play'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p>No episodes found for this podcast.</p>
              )}
            </div>
          </div>

          <div className="right-column">
            <div className="podcast-info-card">
              <img
                src={podcastInfo.image || podcastInfo.artwork || 'https://via.placeholder.com/250?text=No+Image'}
                alt={podcastInfo.title || 'Podcast art'}
                className="podcast-image-large"
              />
              <h2 className="podcast-title-main">{podcastInfo.title || 'No Title'}</h2>
              <div className="podcast-actions">
                <button className="global-button" onClick={handleToggleFavourite} disabled={!currentUser || !podcastInfo.id}>
                  <span className="icon-placeholder">{isFavourite ? '★' : '☆'}</span>
                  {isFavourite ? 'Saved' : 'Bookmark'}
                </button>
                <button className="action-button" onClick={() => alert('Share (placeholder)')}>
                  <span className="icon-placeholder">🔗</span>
                  Share
                </button>
              </div>
              <div className="podcast-description-wrapper">
                <p className={`podcast-description-details ${isDescriptionExpanded ? 'expanded' : 'collapsed'}`}>
                  {(podcastInfo.description || 'No description available.').replace(/<[^>]+>/g, '')}
                </p>
                {podcastInfo.description && podcastInfo.description.length > 150 && (
                  <button
                    className="show-more-button"
                    onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                  >
                    {isDescriptionExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PodcastDetailsPage;